import React, { useState, useEffect } from "react";
import { Box, Typography, Card, Container, useTheme } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useFundsBlacklistState } from "../../hooks/useOptimizedState";
import FundsBlacklistTable from "./FundsBlacklistTable";
import FundsBlacklistDialog from "./FundsBlacklistDialog";
import { Helmet } from "react-helmet";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import {
  hashGetBlacklistCritera,
} from "../../component/Methods";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import {
  consolidateRules,
  getFundType,
} from "../../component/Methods/mainFunctions";

const FundsBlacklist = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [selectedFund, setSelectedFund] = useState("");

  // Use optimized hook to prevent unnecessary re-renders
  const { funds, strategies, managers } = useFundsBlacklistState();

  // Parse blacklist rules from funds state
  const parseBlacklistRules = () => {
    if (!funds || !Array.isArray(funds)) return [];

    if (funds && funds.length > 0) {
      if (funds[0].blacklist && funds[0].blacklist.rules) {
      }
    }

    const allRules = [];
    const ruleGroups = new Map(); // To group similar rules

    funds.forEach((fund) => {
      if (
        fund.blacklist &&
        fund.blacklist.rules &&
        Array.isArray(fund.blacklist.rules)
      ) {
        fund.blacklist.rules.forEach((rule) => {
          // Get strategy name or "ALL"
          let strategyName = "ALL";
          if (rule.strategy && strategies) {
            // Try different possible ID field names for strategies
            const strategy = strategies.find(
              (s) =>
                s.id === rule.strategy ||
                s.strategyId === rule.strategy ||
                s._id === rule.strategy
            );
            strategyName = strategy ? strategy.name : "Unknown Strategy";
          }

          // Get ticker symbol or "ALL"
          let tickerSymbol = "ALL";
          if (rule.ticker && rule.ticker.symbol) {
            tickerSymbol = rule.ticker.symbol.toUpperCase();
          }

          // Generate human-readable rule text
          const ruleText = hashGetBlacklistCritera(rule.type);
          let humanReadableRule = "";

          if (ruleText === "NotAllowed") {
            humanReadableRule = "These tickers are not allowed to be traded";
          } else if (ruleText === "Sectors") {
            // Handle sectors array
            if (
              rule.sectors &&
              Array.isArray(rule.sectors) &&
              rule.sectors.length > 0
            ) {
              const nonEmptySectors = rule.sectors.filter(
                (sector) => sector?.trim() !== ""
              );
              if (nonEmptySectors.length > 0) {
                const isSingular = nonEmptySectors.length === 1;
                humanReadableRule = `Stocks in ${
                  isSingular ? "sector" : "sectors"
                } : ${nonEmptySectors.join(", ")} ${
                  isSingular ? "is" : "are"
                } not allowed to be traded`;
              } else {
                humanReadableRule =
                  "Stocks in specific sectors are not allowed";
              }
            } else {
              humanReadableRule = "Stocks in specific sectors are not allowed";
            }
          } else if (ruleText === "Assets") {
            // Handle asset types array
            if (
              rule.assetTypes &&
              Array.isArray(rule.assetTypes) &&
              rule.assetTypes.length > 0
            ) {
              const nonEmptyAssetTypes = rule.assetTypes.filter(
                (asset) => asset?.trim() !== ""
              );
              if (nonEmptyAssetTypes.length > 0) {
                const isSingular = nonEmptyAssetTypes.length === 1;
                humanReadableRule = `Stocks in ${
                  isSingular ? "asset" : "assets"
                } : ${nonEmptyAssetTypes.join(", ")} ${
                  isSingular ? "is" : "are"
                } not allowed to be traded`;
              } else {
                humanReadableRule =
                  "Stocks in specific asset types are not allowed";
              }
            } else {
              humanReadableRule =
                "Stocks in specific asset types are not allowed";
            }
          } else if (
            ["Price", "Allocation", "MarketCap", "Shares", "Volume"].includes(
              ruleText
            )
          ) {
            const operator =
              rule.maximum && rule.minimum && rule.maximum === rule.minimum
                ? "="
                : rule.maximum
                ? ">"
                : rule.minimum
                ? "<"
                : "";

            if (operator === ">") {
              humanReadableRule = `Stocks with ${ruleText.toLowerCase()} greater than ${
                rule.maximum
              } are not allowed`;
            } else if (operator === "<") {
              humanReadableRule = `Stocks with ${ruleText.toLowerCase()} less than ${
                rule.minimum
              } are not allowed`;
            } else if (operator === "=") {
              humanReadableRule = `Stocks with ${ruleText.toLowerCase()} equal to ${
                rule.maximum
              } are not allowed`;
            } else {
              humanReadableRule = `${ruleText} restriction applied`;
            }
          } else {
            humanReadableRule = `${ruleText} rule applied`;
          }

          // Create a key for grouping: fundId + type + strategy + min/max values + assetTypes + sectors
          const groupKey = `${fund.fundId}-${rule.type}-${
            rule.strategy || "no-strategy"
          }-${rule.minimum || "no-min"}-${
            rule.maximum || "no-max"
          }-${JSON.stringify(rule.assetTypes || [])}-${JSON.stringify(
            rule.sectors || []
          )}`;

          if (!ruleGroups.has(groupKey)) {
            ruleGroups.set(groupKey, {
              fund: fund.abbreviation,
              fundId: fund.fundId,
              type: rule.type,
              rule: humanReadableRule,
              strategy: strategyName,
              strategyId: rule.strategy,
              tickers: new Set(),
              assetTypes: new Set(),
              sectors: new Set(),
              minimum: rule.minimum,
              maximum: rule.maximum,
              originalRules: [],
            });
          }

          // Add ticker to the group
          if (tickerSymbol !== "ALL") {
            ruleGroups.get(groupKey).tickers.add(tickerSymbol);
          }

          // Add asset types to the group
          if (rule.assetTypes && Array.isArray(rule.assetTypes)) {
            rule.assetTypes.forEach((asset) => {
              if (asset && asset.trim() !== "") {
                ruleGroups.get(groupKey).assetTypes.add(asset.trim());
              }
            });
          }

          // Add sectors to the group
          if (rule.sectors && Array.isArray(rule.sectors)) {
            rule.sectors.forEach((sector) => {
              if (sector && sector.trim() !== "") {
                ruleGroups.get(groupKey).sectors.add(sector.trim());
              }
            });
          }

          // Store original rule for reference
          ruleGroups.get(groupKey).originalRules.push(rule);
        });
      }
    });

    // Convert grouped rules to display format
    ruleGroups.forEach((group, key) => {
      const tickerList =
        group.tickers.size > 0
          ? Array.from(group.tickers).sort().join(", ")
          : "ALL";
      const assetTypesList =
        group.assetTypes.size > 0
          ? Array.from(group.assetTypes).sort().join(", ")
          : "";
      const sectorsList =
        group.sectors.size > 0
          ? Array.from(group.sectors).sort().join(", ")
          : "";

      // Update the rule text if we have asset types or sectors
      let finalRule = group.rule;
      if (group.type === 6 && assetTypesList) {
        // Assets type
        const isSingular = group.assetTypes.size === 1;
        finalRule = `Stocks in ${
          isSingular ? "asset" : "assets"
        } : ${assetTypesList} ${
          isSingular ? "is" : "are"
        } not allowed to be traded`;
      } else if (group.type === 5 && sectorsList) {
        // Sectors type
        const isSingular = group.sectors.size === 1;
        finalRule = `Stocks in ${
          isSingular ? "sector" : "sectors"
        } : ${sectorsList} ${
          isSingular ? "is" : "are"
        } not allowed to be traded`;
      }

      const displayRule = {
        id: key,
        fund: group.fund,
        fundId: group.fundId,
        type: group.type,
        rule: finalRule,
        strategy: group.strategy,
        strategyId: group.strategyId,
        ticker: tickerList,
        assetTypes: assetTypesList,
        sectors: sectorsList,
        minimum: group.minimum,
        maximum: group.maximum,
        originalRules: group.originalRules,
      };

      allRules.push(displayRule);
    });

    return allRules;
  };

  const blacklistRules = parseBlacklistRules();

  const handleAdd = (fund) => {
    setSelectedFund(fund);
    setRecordForEdit(null);
    setOpenDialog(true);
  };

  const handleEdit = (rule) => {
    // Use the first original rule from the display rule for editing
    const originalRule =
      rule.originalRules && rule.originalRules.length > 0
        ? rule.originalRules[0]
        : rule;

    // Convert the original rule to the format expected by the dialog
    const editRule = {
      type: originalRule.type,
      ...(originalRule.strategy && { strategy: originalRule.strategy }),
      ...(originalRule.ticker && { ticker: rule.ticker }),
      ...(originalRule.minimum !== undefined && {
        minimum: originalRule.minimum,
      }),
      ...(originalRule.maximum !== undefined && {
        maximum: originalRule.maximum,
      }),
      ...(originalRule.sectors && { sectors: originalRule.sectors }),
      ...(originalRule.assetTypes && { assetTypes: originalRule.assetTypes }),
      ...(originalRule.unit && { unit: originalRule.unit }),
      ...(originalRule.rule && { rule: rule }),
    };

    setRecordForEdit(editRule);
    setOpenDialog(true);
  };
 

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRecordForEdit(null);
  };

  const handleDelete = (selectedRule) => {
    if (window.confirm("Are you sure you want to delete this rule?")) {
      // Get all original rules for this consolidated rule
      const originalRules =
        selectedRule.originalRules && selectedRule.originalRules.length > 0
          ? selectedRule.originalRules
          : [selectedRule];

      const data = {
        action: "modify_blacklist",
        data: {
          rules: {
            add: [],
            modify: [],
            remove: [],
          },
        },
      };

      // Process each original rule
      originalRules.forEach((originalRule) => {
        const ruleObj = {
          type: originalRule.type,
        };

        // Add strategy if it exists
        if (originalRule.strategy) {
          ruleObj.strategy = originalRule.strategy;
        }

        // Add ticker if it exists
        if (originalRule.ticker) {
          ruleObj.ticker = originalRule.ticker.symbol || originalRule.ticker;
        }

        data.data.rules.remove.push(ruleObj);
      });

      const fundId = selectedRule.fundId;
      const fundType = getFundType(fundId, managers);
      if (fundType.blacklistType === "sma") {
        data.data.smaId = fundType.sma;
      } else if (fundType.blacklistType === "fund") {
        data.data.fundId = fundType.fund;
      } else if (fundType.blacklistType === "manager") {
        data.data.managerId = fundType.manager;
      }

      sendWebSocketMessage(JSON.stringify(data));

      dispatch(addActiveRequests("modify_blacklist"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Updating Blacklist"));
    }
  };
  return (
    <>
      <Helmet>
        <title>Funds Blacklist | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Funds Blacklist
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage blacklist rules for funds and strategies
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <FundsBlacklistTable
              rules={blacklistRules}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              selectedFund={selectedFund}
              onFundChange={setSelectedFund}
            />
          </Card>
        </Container>

        <FundsBlacklistDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onSave={() => {}} // Empty function since dialog handles its own save
          editedRule={recordForEdit}
          selectedFund={
            selectedFund
              ? funds?.find((fund) => fund.abbreviation === selectedFund)
                  ?.fundId
              : null
          }
          title={recordForEdit ? "Edit Blacklist Rule" : "Add Blacklist Rule"}
          blacklistData={
            selectedFund
              ? consolidateRules(
                  funds?.find((fund) => fund.abbreviation === selectedFund)
                    ?.blacklist || { rules: [] }
                )
              : null
          }
        />
      </Box>
    </>
  );
};

export default FundsBlacklist;
